import { useEffect, useState } from 'react';

import { passApi, type PassBuyerDetail } from '../../services/passApi';

interface PassBuyersModalProps {
  passId: string;
  passTitle: string;
  getToken: () => Promise<string | undefined>;
  onClose: () => void;
  isHostContext?: boolean;
}

function BuyerStatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
    used_up: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Used Up' },
    expired: { bg: 'bg-red-100', text: 'text-red-800', label: 'Expired' },
    refunded: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Refunded' },
  };
  const c = config[status] || config.active;
  return (
    <span
      className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${c.bg} ${c.text}`}
    >
      {c.label}
    </span>
  );
}

function formatDateTime(dateStr: string) {
  const d = new Date(dateStr);
  const date = d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const formatTime = (dt: Date) => {
    const h = dt.getHours();
    const m = dt.getMinutes();
    const period = h >= 12 ? 'pm' : 'am';
    const hour = h % 12 || 12;
    return m === 0 ? `${hour}${period}` : `${hour}:${m.toString().padStart(2, '0')}${period}`;
  };
  return `${date} ${formatTime(d)}`;
}

function formatSessionDateTime(startTime: string, endTime: string) {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const date = start.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const formatTime = (d: Date) => {
    const h = d.getHours();
    const m = d.getMinutes();
    const period = h >= 12 ? 'pm' : 'am';
    const hour = h % 12 || 12;
    return m === 0 ? `${hour}${period}` : `${hour}:${m.toString().padStart(2, '0')}${period}`;
  };
  return `${date} ${formatTime(start)}-${formatTime(end)}`;
}

export default function PassBuyersModal({
  passId,
  passTitle,
  getToken,
  onClose,
  isHostContext = false,
}: PassBuyersModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [buyers, setBuyers] = useState<PassBuyerDetail[]>([]);
  const [expandedBuyer, setExpandedBuyer] = useState<string | null>(null);

  useEffect(() => {
    const fetchBuyers = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = await getToken();
        if (!token) throw new Error('No access token');
        const data = await passApi.getPassBuyers(passId, token);
        setBuyers(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load buyers');
      } finally {
        setLoading(false);
      }
    };

    fetchBuyers();
  }, [passId, getToken]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{passTitle}</h2>
            <p className="text-sm text-gray-500">
              {buyers.length} buyer{buyers.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64 text-red-600">{error}</div>
          ) : buyers.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-500">No buyers yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Buyer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Used / Total
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paid
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Purchased
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bookings
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {buyers.map((buyer) => {
                    const totalSessions = buyer.usedSessions + buyer.remainingSessions;
                    const isExpanded = expandedBuyer === buyer.id;
                    const confirmedBookings = buyer.bookings.filter(
                      (b) => b.status === 'CONFIRMED'
                    );

                    return (
                      <tr key={buyer.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {buyer.user.name || 'N/A'}
                          </div>
                          {!isHostContext && (
                            <div className="text-xs text-gray-500">{buyer.user.email}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <BuyerStatusBadge status={buyer.displayStatus} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <span className="font-medium">{buyer.usedSessions}</span>
                          <span className="text-gray-400"> / {totalSessions}</span>
                          {buyer.remainingSessions > 0 && buyer.displayStatus === 'active' && (
                            <span className="text-xs text-green-600 ml-1">
                              ({buyer.remainingSessions} left)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          ${(buyer.paymentAmount / 100).toFixed(2)}
                          {buyer.storeCreditUsed ? (
                            <span className="text-xs text-gray-500 block">
                              +${(buyer.storeCreditUsed / 100).toFixed(2)} credit
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {formatDateTime(buyer.purchasedAt)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {confirmedBookings.length > 0 ? (
                            <button
                              onClick={() => setExpandedBuyer(isExpanded ? null : buyer.id)}
                              className="text-blue-600 hover:underline text-sm"
                            >
                              {confirmedBookings.length} booking
                              {confirmedBookings.length !== 1 ? 's' : ''}
                              <svg
                                className={`inline w-3 h-3 ml-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                          ) : (
                            <span className="text-gray-400">None</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Expanded booking details (shown below the table row) */}
              {expandedBuyer && (
                <div className="mt-2 mb-4 mx-4 bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Booking History</h4>
                  <div className="space-y-1">
                    {buyers
                      .find((b) => b.id === expandedBuyer)
                      ?.bookings.map((booking) => (
                        <div
                          key={booking.id}
                          className="flex items-center justify-between text-sm bg-white px-3 py-2 rounded"
                        >
                          <span className="text-gray-700">
                            {formatSessionDateTime(
                              booking.session.startTime,
                              booking.session.endTime
                            )}
                          </span>
                          <span
                            className={`text-xs font-medium ${
                              booking.status === 'CONFIRMED'
                                ? 'text-green-600'
                                : booking.status === 'CANCELLED'
                                  ? 'text-red-600'
                                  : 'text-gray-600'
                            }`}
                          >
                            {booking.status}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && buyers.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 text-sm text-gray-500">
            Total {buyers.length} buyer{buyers.length !== 1 ? 's' : ''} ·{' '}
            {buyers.reduce((sum, b) => sum + b.usedSessions, 0)} sessions used ·{' '}
            {buyers.reduce((sum, b) => sum + b.remainingSessions, 0)} sessions remaining
          </div>
        )}
      </div>
    </div>
  );
}
